import {
    Activity,
    AlertTriangle,
    BadgeCheck,
    BarChart3,
    Bell,
    Box,
    Cable,
    ClipboardCheck,
    ClipboardList,
    Clock,
    Cog,
    Cpu,
    Database,
    Download,
    FileText,
    Fingerprint,
    FlaskConical,
    FolderOpen,
    Gauge,
    GitCompare,
    Handshake,
    History,
    KeyRound,
    LayoutDashboard,
    LayoutTemplate,
    List,
    MailPlus,
    Network,
    Server,
    Settings2,
    Shield,
    ShieldCheck,
    Sparkles,
    Tags,
    User,
    UserCheck,
    UserPlus,
    Users,
    Wand2,
} from 'lucide-react'
import type {LucideIcon} from 'lucide-react'

// WHY: Single source of truth for concept-to-icon mapping. Each conceptual entity
// (identity, system, party, role, accessGrant, ...) gets exactly one Lucide icon
// here. UI surfaces (sidebar nav, page headers, card sections) reference the
// concept by key — never import a Lucide icon directly for a concept-shaped slot.
//
// Drift cases resolved when this registry was introduced (issue 297):
//   - identity: Analytics sidebar (`byIdentity`) and the `EffectiveAccessByIdentity`
//     page used `Users`, but `IdentityManagement` sidebar, `Identities` page header,
//     and `IdentityDetail` (HeaderIcon fallback) all used `Fingerprint`. Aligned to
//     Fingerprint (3+ sites) — `Users` becomes the `party` icon exclusively.
//   - accessRequest: AccessRequestWizard previously used ClipboardList while
//     AccessRequests / AccessRequestDetail / sidebar all used ClipboardCheck.
//     Aligned to ClipboardCheck (3 sites > 1 site).
//   - accessGrant (IdentityGrantsDetail page): kept Shield as the canonical
//     "effective access grant" icon — distinct from `identity` (Fingerprint).
//     The Analytics 'by Identity' page (`EffectiveAccessByIdentity`) is a list
//     of identities, not grants, so it routes through `identity` not `accessGrant`.
//   - restrictionRuleRequest: AccessGovernance sidebar previously used
//     ClipboardList while every page header (RestrictionRuleRequests, Create,
//     Detail, Edit) used Shield. Aligned to Shield (4 sites > 1 site) so the
//     concept stays consistent across surfaces, even though it makes the sidebar
//     show two adjacent Shield items (restrictionRules + restrictionRuleRequests)
//     distinguished by label.
//
// Multiple concepts may share an icon (e.g. `errorLog` and `restrictionRuleViolation`
// both use AlertTriangle). The mapping is concept -> icon, not icon -> concept.
export const CONCEPT_ICON = {
    accessGrant: Shield,
    accessProfile: KeyRound,
    accessRequest: ClipboardCheck,
    account: KeyRound,
    adminRole: Shield,
    analyticsDashboard: BarChart3,
    approvalPolicy: ShieldCheck,
    assignmentDecision: ShieldCheck,
    attributeDefinition: Tags,
    authUser: User,
    autoAssignRule: Sparkles,
    autoAssignRuleRequest: Wand2,
    backgroundJob: Cog,
    certificationCampaign: BadgeCheck,
    certificationDashboard: LayoutDashboard,
    certificationReview: BarChart3,
    cache: Database,
    clusterNode: Server,
    compareGrants: GitCompare,
    connectorInbound: Download,
    connectorOutbound: Cable,
    constraint: Gauge,
    dashboard: LayoutDashboard,
    delegation: Handshake,
    enforcementObject: Shield,
    errorLog: AlertTriangle,
    executionDetail: FileText,
    formLayout: LayoutTemplate,
    'function-group': ClipboardList,
    functionGroupRequest: ClipboardList,
    identity: Fingerprint,
    jobConfiguration: Settings2,
    jobExecution: History,
    lifecycleEvents: Activity,
    llmPrompt: Sparkles,
    myApproval: ShieldCheck,
    myRequest: ClipboardList,
    notification: Bell,
    notificationTemplate: MailPlus,
    onboarding: UserPlus,
    ownership: ClipboardList,
    party: Users,
    provisioningHistory: History,
    provisioningTask: Clock,
    provisioningTaskDetail: Cpu,
    rehireReview: UserCheck,
    relationshipSchema: Network,
    report: FileText,
    reportCategory: FolderOpen,
    requestInspector: Activity,
    resource: Box,
    restrictionRule: Shield,
    restrictionRuleRequest: Shield,
    restrictionRuleViolation: AlertTriangle,
    roleAssignment: Users,
    scriptExecuteLog: FileText,
    serviceAccount: KeyRound,
    system: Server,
    testData: FlaskConical,
    typeValue: Database,
    valueList: List,
    violation: AlertTriangle,
} as const satisfies Record<string, LucideIcon>

export type ConceptKey = keyof typeof CONCEPT_ICON
